/* eslint-disable security/detect-object-injection */
import { Component, Event, type EventEmitter, Element, Host, Listen, Prop, State, h, Watch } from '@stencil/core'

let id = 0
@Component({
  tag: 'cobalt-tabs-vertical',
  styleUrl: 'cobalt-tabs-vertical.css'
})
export class CobaltTabsVertical {
  tabsId = `tabs-vertical-${id++}`
  @Element() element: HTMLElement

  /**
   * Set a tab as active by providing the index (tab can not be disabled).
   */
  @Prop({ mutable: true }) activeTabIndex: number = 0
  /**
   * Change the width of the tab list.
   */
  @Prop({ mutable: true }) width: 'small' | 'medium' | 'large' = 'small'

  @State() internalActiveTabIndex: number = 0
  @State() internalFocusedTabIndex: number = 0
  @State() tabRefs: HTMLElement[] = []
  @State() tabHeaderRefs: HTMLElement[] = []
  @State() tabObjects: any[] = []

  @Event() cobaltTabChange: EventEmitter

  setActiveAndFocusedTab(index: number): void {
    this.internalActiveTabIndex = index
    this.internalFocusedTabIndex = index
  }

  @Watch('activeTabIndex')
  activeTabIndexChanged(newValue: number, oldValue: number): void {
    if (newValue !== oldValue) {
      this.setActiveAndFocusedTab(newValue)
    }
  }

  componentWillLoad(): void {
    if (this.activeTabIndex) this.internalActiveTabIndex = this.activeTabIndex
    if (this.internalActiveTabIndex !== this.activeTabIndex) {
      this.cobaltTabChange.emit({ previousTab: this.internalActiveTabIndex, currentTab: this.activeTabIndex })
    }
    this.internalFocusedTabIndex = this.internalActiveTabIndex
    this.onSlotChange()
  }

  // when a slot is removed / added
  @Listen('slotchange')
  onSlotChange(): void {
    const tabs = this.element.querySelectorAll('cobalt-tab-panel')
    this.tabObjects = Array.from(tabs).map(tab => {
      const pillNumber = tab.getAttribute('notification-pill-number') !== '' ? tab.getAttribute('notification-pill-number') : 'undefined'
      return {
        label: tab.getAttribute('label'),
        iconName: tab.getAttribute('icon-name'),
        notificationPillNumber: pillNumber,
        disabled: tab.getAttribute('disabled') === 'true'
      }
    })
    // Ensure icons have the right variant
    this.setIconDependingOnSelectedTab()

    this.tabRefs = Array.from(tabs) as HTMLElement[]
    this.tabRefs.forEach((tab, index) => {
      tab.setAttribute('slot', `${this.tabsId}_tab-${index}`)
    })
  }

  setIconDependingOnSelectedTab(): void {
    this.tabObjects.forEach((tabPanel, index) => {
      if (!tabPanel.iconName) return
      if (index !== this.internalActiveTabIndex || this.tabObjects[index].disabled) {
        this.tabObjects[index].iconName = tabPanel.iconName.replace('-solid', '-outline')
      } else {
        this.tabObjects[index].iconName = tabPanel.iconName.replace('-outline', '-solid')
      }
    })
  }

  componentDidLoad(): void {
    // Add keyboard event listeners for each tab header
    this.tabHeaderRefs.forEach((tab, index) => {
      tab.addEventListener('focus', this.onTabFocus(index))
    })

    document.addEventListener('click', event => {
      if (!event.composedPath().includes(this.element)) {
        this.updateTabFocusability()
      }
    })
  }

  onTabFocus(index) {
    return () => {
      this.internalFocusedTabIndex = index
    }
  }

  disconnectedCallback(): void {
    // Remove keyboard event listeners when component is unmounted
    this.tabHeaderRefs.forEach((tab, index) => {
      tab.removeEventListener('focus', this.onTabFocus(index))
    })
  }

  componentDidUpdate(): void {
    this.updateTabFocusability()
  }

  private updateTabFocusability(): void {
    this.tabHeaderRefs.forEach((tab, index) => {
      tab.tabIndex = index === this.internalActiveTabIndex ? 0 : -1
    })
  }

  private focusNextTab(): void {
    let nextIndex = this.internalFocusedTabIndex + 1
    while (nextIndex < this.tabHeaderRefs.length && this.tabObjects[nextIndex].disabled) {
      nextIndex++
    }
    if (nextIndex >= 0 && nextIndex < this.tabHeaderRefs.length) {
      this.internalFocusedTabIndex = nextIndex
      this.tabHeaderRefs[nextIndex].focus()
    }
  }

  private focusPreviousTab(): void {
    let prevIndex = this.internalFocusedTabIndex - 1
    while (prevIndex >= 0 && this.tabObjects[prevIndex].disabled) {
      prevIndex--
    }
    if (prevIndex >= 0 && prevIndex < this.tabHeaderRefs.length) {
      this.internalFocusedTabIndex = prevIndex
      this.tabHeaderRefs[prevIndex].focus()
    }
  }

  private getTabItemClass(index: number): string {
    const isActive = index === this.internalActiveTabIndex && !this.tabObjects[index].disabled
    const isDisabled = this.tabObjects[index].disabled
    return `cobalt-tab-item ${isActive ? 'tab-item--selected' : ''} ${isDisabled ? 'tab-item--disabled' : ''}`
  }

  private handleClick(tab, index): void {
    if (tab.disabled) return
    this.cobaltTabChange.emit({ previousTab: this.internalActiveTabIndex, currentTab: index })
    this.internalActiveTabIndex = index
    // Ensure to get back to original icon
    this.setIconDependingOnSelectedTab()
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        // Shift + Tab
        if (this.internalFocusedTabIndex === 0) {
          // Allow default behavior to move focus out of component
        } else {
          event.preventDefault()
          this.focusPreviousTab()
        }
      } else {
        // Tab
        if (this.internalFocusedTabIndex === this.tabHeaderRefs.length - 1) {
          // Allow default behavior to move focus out of component
        } else {
          event.preventDefault()
          this.focusNextTab()
        }
      }
    } else if (event.key === 'Enter' && this.internalFocusedTabIndex !== -1 && !this.tabObjects[this.internalFocusedTabIndex].disabled) {
      const previouslyActiveTabIndex = this.internalActiveTabIndex
      this.internalActiveTabIndex = this.internalFocusedTabIndex
      this.cobaltTabChange.emit({ previousTab: previouslyActiveTabIndex, currentTab: this.internalFocusedTabIndex })
    }
  }

  render(): any {
    return (
      <Host>
        <div id={this.tabsId} class="cobalt-tabs cobalt-tabs--vertical">
          <ul role="tablist" class={`cobalt-tabs-list cobalt-tabs-list--${this.width} typography-monoline-medium`}>
            {this.tabObjects.map((tab, index) => (
              <li
                id={`tab-label-${index}`}
                class={this.getTabItemClass(index)}
                ref={el => (this.tabHeaderRefs[index] = el)}
                tabindex="0"
                onClick={() => {
                  this.handleClick(tab, index)
                }}
                aria-selected={index === this.internalActiveTabIndex ? 'true' : 'false'}
                aria-disabled={tab.disabled ? 'true' : 'false'}
                role="tab"
                aria-controls={`${this.tabsId}_tab-panel-${index}`}
              >
                <div class="tab-label-content">
                  {tab.iconName && <cobalt-icon name={tab.iconName} size="number-size-icon-medium"></cobalt-icon>}
                  <span class="tab-label">{tab.label}</span>
                  {tab.notificationPillNumber && typeof tab.notificationPillNumber !== 'undefined' && (
                    <cobalt-notification-pill number={tab.notificationPillNumber}></cobalt-notification-pill>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div class="tab-content">
            {Array.from(this.tabObjects).map((_, index) => (
              <div
                id={`${this.tabsId}_tab-panel-${index}`}
                role="tabpanel"
                aria-labelledby={`tab-label-${index}`}
                style={{ display: index === this.internalActiveTabIndex && !this.tabObjects[index].disabled ? 'block' : 'none' }}
              >
                <slot name={`${this.tabsId}_tab-${index}`} />
              </div>
            ))}
          </div>
        </div>
      </Host>
    )
  }
}
